import {
  AGENTS,
  CLIENT_CONTACT,
  HERO_PROJECT_ID,
  OFFICES,
  PHASES,
  PROJECTS,
  agentById,
  personById,
  phaseIndex,
  projectById,
  type AgentDef,
  type OfficeCode,
  type Phase,
  type PhaseKey,
  type Project,
} from '@/demo/data'

/**
 * Surface 2.10 — Playbook configuration. The delivery method itself, written
 * down per contract type: which phases apply, which gates need a client
 * signature, what each phase must produce, which agents are on and at what
 * autonomy.
 *
 * What this replaces is the unwritten method in the head of each office lead.
 * When four countries deliver the same playbook, work can genuinely be routed
 * to where the skill is — so everything the page shows resolves from the seed
 * (`@/demo/data`) or from this module, and the component carries no literal.
 *
 * DERIVED, NOT TYPED:
 *  - the phase rows are `PHASES` — the group's seven phases with their own
 *    gate type; the playbook cannot show a phase the lifecycle does not have;
 *  - who signs follows from the gate type, not from a per-row decision;
 *  - the agents on a phase are `AGENTS` filtered by that phase (an agent with
 *    phase `all` runs in every one), and each carries its OWN default
 *    autonomy — so the table and the agent library can never disagree;
 *  - the contract-type chips are the distinct contract types the portfolio
 *    actually runs, in portfolio order.
 */

/** A run of prose in which some words are real destinations. */
export type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'link'; label: string; href: string }

/* ── Routes ─────────────────────────────────────────────────────────────── */

export const PROJECTS_HREF = '/projects'
export const PORTFOLIO_HEALTH_HREF = '/projects/health'
export const PORTAL_HREF = '/portal'
export const agentHref = (agentId: string): string => `/library/agents/${agentId}`
export const agentGuardrailsHref = (agentId: string): string => `${agentHref(agentId)}/guardrails`
export const personHref = (personId: string): string => `/resources/people/${personId}`

/* ── The playbook this surface expands ──────────────────────────────────── */

export type ContractType = Project['contractType']

/** The contract types the portfolio runs, in portfolio order — never typed. */
export const CONTRACT_TYPES: readonly ContractType[] = PROJECTS.reduce<ContractType[]>(
  (types, project) => (types.includes(project.contractType) ? types : [...types, project.contractType]),
  []
)

/** The hero project's own contract type is the one expanded in full. */
export const EXEMPLAR_TYPE: ContractType = projectById(HERO_PROJECT_ID).contractType

export const PLAYBOOK_VERSION = 'v6'

/** Live projects delivering this playbook version (deck figure). */
export const APPLIED_PROJECT_COUNT = 9

/** Publishing has an owner: the practice lead who signs the version off. */
const OWNER = personById('niels-jansen')
export const PLAYBOOK_OWNER = {
  id: OWNER.id,
  name: OWNER.name,
  meta: `${OWNER.role} · ${OWNER.office}`,
  href: personHref(OWNER.id),
} as const

const firstWord = (value: string): string => value.split(' ')[0] ?? value

export const PLAYBOOK_CHROME = {
  moduleLabel: 'PX · Configuration',
  breadcrumb: `Configuration › Playbooks › ${EXEMPLAR_TYPE}`,
  contextPill: `${firstWord(EXEMPLAR_TYPE).toUpperCase()} · ${PLAYBOOK_VERSION.toUpperCase()}`,
} as const

export const PLAYBOOK_COPY = {
  title: 'Playbook configuration',
  lede: 'The delivery method itself — per contract type, in writing, versioned.',
  replaces:
    'What this replaces is the unwritten method in the head of each office lead. When four countries deliver the same playbook, work can be routed to where the skill is.',
  chipsLegend: 'Contract type',
  appliedPrefix: `${EXEMPLAR_TYPE} · ${PLAYBOOK_VERSION} — applied to `,
  appliedLink: `${APPLIED_PROJECT_COUNT} projects`,
  appliedSuffix: '.',
  ownerPrefix: 'Owner ',
  summaryHint: 'summarised — the phases it keeps and the phases it never opens',
} as const

/* ── Phase helpers ──────────────────────────────────────────────────────── */

/** 1-based position in the lifecycle, as the offices say it: "phase 2 of 7". */
export const phaseNumber = (key: PhaseKey): number => phaseIndex(key) + 1

export const phaseByKey = (key: PhaseKey): Phase => {
  const phase = PHASES.find((p) => p.key === key)
  if (!phase) throw new Error(`Unknown phase: ${key}`)
  return phase
}

export const phasePosition = (key: PhaseKey): string =>
  `Phase ${phaseNumber(key)} of ${PHASES.length}`

/** Every agent that runs in a phase — `all` spans the lifecycle. */
export const agentsForPhase = (key: PhaseKey): readonly AgentDef[] =>
  AGENTS.filter((agent) => agent.phase === key || agent.phase === 'all')

/* ── Who signs — a consequence of the gate type, not a per-row choice ───── */

export interface Signatory {
  role: string
  name: string
  meta: string
  href: string
  title: string
}

const DELIVERY_LEAD_PERSON = personById(projectById(HERO_PROJECT_ID).leadId)

const CLIENT_SPONSOR: Signatory = {
  role: 'Client sponsor',
  name: CLIENT_CONTACT.name,
  meta: `${CLIENT_CONTACT.role} · ${CLIENT_CONTACT.org}`,
  href: PORTAL_HREF,
  title: `The client portal — where ${CLIENT_CONTACT.name} signs the gate`,
}

const DELIVERY_LEAD: Signatory = {
  role: 'Delivery lead',
  name: DELIVERY_LEAD_PERSON.name,
  meta: `${DELIVERY_LEAD_PERSON.role} · ${DELIVERY_LEAD_PERSON.office}`,
  href: personHref(DELIVERY_LEAD_PERSON.id),
  title: `${DELIVERY_LEAD_PERSON.name}'s profile`,
}

const PRACTICE_LEAD: Signatory = {
  role: 'Practice lead',
  name: OWNER.name,
  meta: `${OWNER.role} · ${OWNER.office}`,
  href: personHref(OWNER.id),
  title: `${OWNER.name}'s profile`,
}

/** Client gates go to the sponsor; internal gates stay in the delivery line. */
export const SIGNATORY_BY_GATE: Record<Phase['gate'], Signatory> = {
  'Client sign-off': CLIENT_SPONSOR,
  Internal: DELIVERY_LEAD,
  Quarterly: PRACTICE_LEAD,
}

/* ── The per-phase table ────────────────────────────────────────────────── */

/**
 * What a phase must put on the table before its gate can be offered. This is
 * the playbook's own text — the one thing on this surface the lifecycle seed
 * does not already carry.
 */
const MUST_PRODUCE: Record<PhaseKey, string> = {
  discover:
    'A triaged requirements register, the fit-gap split behind it, and a costed change-control clause the client has read.',
  design:
    'The solution blueprint: every approved requirement mapped to standard, reusable library or new build, with the integration map.',
  build:
    'Configured modules in a demo environment, each traceable to the requirement that asked for it, merged by a named person.',
  migrate:
    'Field maps, load scripts, and a rehearsed trial load with its validation-error count written down.',
  test: 'Test cases per approved requirement, a clean regression pass, and a UAT result per client process owner.',
  'go-live':
    'The cutover runbook with named owners and timings, the rollback point, and a training record per user group.',
  maintain:
    'A quarterly service review: tickets against SLA, the upgrade scan, and a costed proposal for what comes next.',
}

export interface PhaseRow {
  phase: Phase
  position: string
  produces: string
  signatory: Signatory
  agents: readonly AgentDef[]
}

export const PHASE_ROWS: readonly PhaseRow[] = PHASES.map((phase) => ({
  phase,
  position: phasePosition(phase.key),
  produces: MUST_PRODUCE[phase.key],
  signatory: SIGNATORY_BY_GATE[phase.gate],
  agents: agentsForPhase(phase.key),
}))

export const PHASE_TABLE = {
  label: 'Phases & gates',
  caption: `The ${PHASES.length} phases of ${EXEMPLAR_TYPE} ${PLAYBOOK_VERSION}: what each phase must produce, the gate that closes it, who signs that gate, and the agents switched on with the autonomy they run at.`,
  columns: {
    phase: 'Phase',
    produces: 'Must produce',
    gate: 'Gate',
    signedBy: 'Signed by',
    agents: 'Agents switched on',
    autonomy: 'Autonomy',
  },
  agentLinkTitle: (name: string) => `Open the written contract for ${name}`,
  autonomyTitle: (agent: AgentDef) => `${agent.name} runs at ${agent.defaultAutonomy} by default`,
  spanningNote: 'every phase',
} as const

/* ── Gate rules ─────────────────────────────────────────────────────────── */

export interface GateRule {
  title: string
  prose: readonly Segment[]
}

export const GATE_RULES = {
  label: 'Gate rules',
  lede: 'The same two sentences on every project, whatever the office.',
  rules: [
    {
      title: 'Order holds',
      prose: [
        {
          kind: 'text',
          text: 'A phase cannot open until the previous gate is signed. Work can be prepared early and often is — a rehearsal is not an open phase.',
        },
      ],
    },
    {
      title: 'Overrides are allowed, and never quiet',
      prose: [
        {
          kind: 'text',
          text: 'A delivery lead can open a phase before its gate is signed. The override is logged with a reason and a name, it stays on the project record, and it is flagged on ',
        },
        { kind: 'link', label: 'portfolio health', href: PORTFOLIO_HEALTH_HREF },
        { kind: 'text', text: ' until the signature catches up.' },
      ],
    },
  ] as readonly GateRule[],
} as const

/* ── Country layers ─────────────────────────────────────────────────────── */

const officeByCode = (code: OfficeCode) => {
  const office = OFFICES.find((o) => o.code === code)
  if (!office) throw new Error(`Unknown office: ${code}`)
  return office
}

export interface CountryLayer {
  code: OfficeCode
  officeName: string
  phase: Phase
  position: string
  adds: string
  detail: string
  ownerName: string
  ownerHref: string
  ownerTitle: string
}

const layer = (
  code: OfficeCode,
  phaseKey: PhaseKey,
  adds: string,
  detail: string,
  ownerId: string
): CountryLayer => {
  const office = officeByCode(code)
  const owner = personById(ownerId)
  return {
    code: office.code,
    officeName: office.name,
    phase: phaseByKey(phaseKey),
    position: phasePosition(phaseKey),
    adds,
    detail,
    ownerName: owner.name,
    ownerHref: personHref(owner.id),
    ownerTitle: `${owner.name} · ${owner.role}, ${owner.office}`,
  }
}

export const COUNTRY_LAYERS = {
  label: 'Country layers',
  lede: 'What an office adds on top of the group playbook — and the limit on what it may do.',
  layers: [
    layer(
      'BE',
      'design',
      'Payroll & social secretariat',
      'The blueprint carries a payroll section and the social-secretariat interface; the Design gate is not offered until both are drafted.',
      'margot-claes'
    ),
    layer(
      'FR',
      'test',
      'Factur-X e-invoicing',
      'A Factur-X conformance run joins the test pack; UAT does not close while it fails.',
      'marc-dubois'
    ),
  ] as readonly CountryLayer[],
  rule: 'A country layer can only add. Neither office can remove a group requirement — an office that needs less raises it as a change to the group playbook, which is where this page comes from.',
  addsLabel: 'Adds to',
  ownerLabel: 'Owner',
} as const

/* ── Change log ─────────────────────────────────────────────────────────── */

const CUTOVER_PLANNER = agentById('cutover-planner')
const TEST_AUTHOR = agentById('test-author')

export interface ChangeEntry {
  version: string
  prose: readonly Segment[]
  effect: string
}

export const CHANGE_LOG = {
  label: 'Change log',
  lede: `Version history for ${EXEMPLAR_TYPE}. Every published version is signed off by the owner and dry-run before it reaches a live project.`,
  entries: [
    {
      version: PLAYBOOK_VERSION,
      prose: [
        { kind: 'link', label: CUTOVER_PLANNER.name, href: agentHref(CUTOVER_PLANNER.id) },
        { kind: 'text', text: ` dropped to ${CUTOVER_PLANNER.defaultAutonomy}.` },
      ],
      effect: `${CUTOVER_PLANNER.reviewGate} — the runbook is a human document and the agent only drafts into it. Clean acceptance was ${CUTOVER_PLANNER.acceptancePct}%.`,
    },
    {
      version: 'v5',
      prose: [
        { kind: 'link', label: TEST_AUTHOR.name, href: agentHref(TEST_AUTHOR.id) },
        { kind: 'text', text: ` raised to ${TEST_AUTHOR.defaultAutonomy}.` },
      ],
      effect: `Clean acceptance of ${TEST_AUTHOR.acceptancePct}% carried it up a rung; the gate stays ${TEST_AUTHOR.reviewGate.toLowerCase()}.`,
    },
    {
      version: 'v4',
      prose: [
        { kind: 'text', text: 'Coverage threshold raised 90 → 95% on the ' },
        { kind: 'link', label: `${TEST_AUTHOR.name} guardrails`, href: agentGuardrailsHref(TEST_AUTHOR.id) },
        { kind: 'text', text: '.' },
      ],
      effect: `Applies to every ${phaseByKey('test').name}-phase run on this playbook, in every office.`,
    },
  ] as readonly ChangeEntry[],
} as const

/* ── The four summarised contract types ─────────────────────────────────── */

export interface ContractSummary {
  type: ContractType
  keeps: readonly PhaseKey[]
  lines: readonly string[]
}

const SUMMARIES: readonly ContractSummary[] = [
  {
    type: 'Time & materials',
    keeps: PHASES.map((p) => p.key),
    lines: [
      'Every phase runs, in the same order, producing the same artefacts.',
      'What changes is the gate: scope is not fixed, so each client sign-off also approves spend to date against the register.',
    ],
  },
  {
    type: 'Upgrade project',
    keeps: ['build', 'migrate', 'test', 'go-live', 'maintain'],
    lines: [
      'The processes are already live, so the two discovery phases never open.',
      'The upgrade scan stands in for fit-gap: it names what breaks between versions, and that list is the scope.',
    ],
  },
  {
    type: 'AMS contract',
    keeps: ['maintain'],
    lines: [
      'One phase, running continuously on the quarterly gate.',
      'Work arrives as tickets against an SLA rather than as a phase plan; a change large enough to need a blueprint leaves AMS and becomes its own project.',
    ],
  },
  {
    type: 'Discovery only',
    keeps: ['discover'],
    lines: [
      'Discover alone, with its client sign-off unchanged.',
      'The engagement ends at the signed scope and the costed options; everything after it is quoted as a separate contract.',
    ],
  },
]

export const CONTRACT_SUMMARY_BY_TYPE: ReadonlyMap<ContractType, ContractSummary> = new Map(
  SUMMARIES.map((summary) => [summary.type, summary])
)

export const summaryFor = (type: ContractType): ContractSummary | undefined =>
  CONTRACT_SUMMARY_BY_TYPE.get(type)

export const keptPhases = (summary: ContractSummary): readonly Phase[] =>
  PHASES.filter((phase) => summary.keeps.includes(phase.key))

export const droppedPhases = (summary: ContractSummary): readonly Phase[] =>
  PHASES.filter((phase) => !summary.keeps.includes(phase.key))

/** Projects in the portfolio currently on a contract type — derived. */
export const projectsOnType = (type: ContractType): readonly Project[] =>
  PROJECTS.filter((project) => project.contractType === type)

export const SUMMARY_CARD = {
  keepsLabel: 'Phases kept',
  dropsLabel: 'Never opens',
  dropsNone: 'No phase is dropped',
  clientGatesLabel: 'Client gates',
  phaseCount: (kept: number) => `${kept} of ${PHASES.length}`,
  seenLabel: 'In the portfolio',
  seenCount: (n: number) => (n === 1 ? '1 project' : `${n} projects`),
  expandHint: 'Expanded in full on this page: ',
} as const

export const clientGateCount = (summary: ContractSummary): number =>
  keptPhases(summary).filter((phase) => phase.gate === 'Client sign-off').length

/* ── Density note ───────────────────────────────────────────────────────── */

export const DENSITY_NOTE: readonly Segment[] = [
  {
    kind: 'text',
    text: `One contract type is expanded here — ${EXEMPLAR_TYPE}, the ${projectsOnType(EXEMPLAR_TYPE).length} live rollouts in this portfolio — and the other ${SUMMARIES.length} are summarised to the phases they keep and the phases they never open, because a second full table would be read as a second method rather than the same one under different terms. Publishing is versioned and has an owner: a change lands as a new version, signed off by ${PLAYBOOK_OWNER.name}, and dry-run against a live project first — the same ` },
  { kind: 'link', label: 'dry-run pattern', href: agentGuardrailsHref('module-scaffolder') },
  { kind: 'text', text: ' the agents use before anything is merged.' },
]
